import { __rest } from "tslib";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// biome-ignore lint/style/useImportType: <explanation>
import { useState, useEffect, forwardRef } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';
import classNames from 'classnames';
import isNil from 'lodash/fp/isNil';
import noop from 'lodash/fp/noop';
import { getOrCreatePortalRoot } from '../../utils/portalRoot';
import useClickOutside from '../../hooks/useClickOutside';
import MenuItems from '../menuItems/MenuItems';
import MenuItemList from '../menuItems/MenuItemList';
import DropdownToggleButton from './DropdownToggleButton';
import SplitCaretButton from './SplitCaretButton';
import Caret from './Caret';
const getPlacement = (pullRight, dropup) => {
    if (pullRight && dropup) {
        return 'top-end';
    }
    if (!pullRight && dropup) {
        return 'top-start';
    }
    if (pullRight && !dropup) {
        return 'bottom-end';
    }
    return 'bottom-start';
};
const ButtonDropdown = forwardRef((props, ref) => {
    const { id = Math.random().toString(36).slice(2, 16), items = [], bsSize = 'md', bsStyle = 'default', variant, disabled = false, iconOnly = false, title, splitButton = false, customDropdown, open, dropup = false, pullRight = false, noCaret = false, onOpen = noop, onClose = noop, onLabelButtonClick = noop, usePortal = false, popperConfig, toggleClassName = '', dropdownClassName, className = '' } = props, remainingProps = __rest(props, ["id", "items", "bsSize", "bsStyle", "variant", "disabled", "iconOnly", "title", "splitButton", "customDropdown", "open", "dropup", "pullRight", "noCaret", "onOpen", "onClose", "onLabelButtonClick", "usePortal", "popperConfig", "toggleClassName", "dropdownClassName", "className"]);
    const [internalOpen, setInternalOpen] = useState(open);
    const [refDropdownToggle, setRefDropdownToggle] = useState(null);
    const [refDropdownMenu, setRefDropdownMenu] = useState(null);
    const [refSplitButtonToggle, setRefSplitButtonToggle] = useState(null);
    const defaultPopperConfig = {
        placement: getPlacement(pullRight, dropup),
        modifiers: [],
    };
    const popperParentRef = splitButton && pullRight ? refSplitButtonToggle : refDropdownToggle;
    const { styles, attributes } = usePopper(popperParentRef, refDropdownMenu, popperConfig || defaultPopperConfig);
    const wrapperRef = useClickOutside(event => {
        if (usePortal) {
            // In case the dropdown is rendered via portal the clickOutside the toggle button element is triggered
            // since the dropdown is not a child of the wrapper element.
            // In this case we need to check if the event target is inside the dropdown-menu and prevent closing
            // the dropdown
            if (!(refDropdownMenu === null || refDropdownMenu === void 0 ? void 0 : refDropdownMenu.contains(event.target))) {
                closeMenu();
            }
        }
        else {
            closeMenu();
        }
    });
    const dropdownRoot = getOrCreatePortalRoot();
    const isUncontrolled = isNil(open);
    const shouldShowCaret = !noCaret && !splitButton && !iconOnly;
    useEffect(() => {
        if (!isUncontrolled) {
            setInternalOpen(open);
        }
    }, [isUncontrolled, open]);
    const toggleOpen = (event) => {
        const isDropdownOpen = isUncontrolled ? internalOpen : open;
        if (isDropdownOpen) {
            closeMenu();
        }
        else {
            openMenu(event);
        }
    };
    const openMenu = (event) => {
        if (isUncontrolled) {
            setInternalOpen(true);
        }
        onOpen(event);
    };
    const closeMenu = () => {
        if (isUncontrolled) {
            setInternalOpen(false);
        }
        onClose();
    };
    const handleSplitLabelButtonClick = () => {
        closeMenu();
        onLabelButtonClick();
    };
    const handleDropdownButtonClick = splitButton ? handleSplitLabelButtonClick : toggleOpen;
    const isOpen = isUncontrolled ? internalOpen : open;
    const wrapperClasses = classNames('dropdown', 'btn-group', isOpen && 'open', className);
    const dropdownClasses = classNames(usePortal && 'dropdown-portal', splitButton && pullRight && 'pull-right', dropdownClassName);
    const dropdownMenu = (_jsx(MenuItemList, Object.assign({ className: dropdownClasses, ref: setRefDropdownMenu, style: styles.popper }, attributes.popper, { children: customDropdown ? customDropdown : _jsx(MenuItems, { items: items, closeMenu: toggleOpen }) })));
    return (_jsxs("div", Object.assign({}, remainingProps, { className: wrapperClasses, ref: wrapperRef }, { children: [_jsx(DropdownToggleButton, Object.assign({ id: id, splitButton: splitButton, bsStyle: bsStyle, bsSize: bsSize, variant: variant, iconOnly: iconOnly, disabled: disabled, ref: setRefDropdownToggle, onClick: handleDropdownButtonClick, outerRef: ref, className: toggleClassName }, { children: _jsxs(_Fragment, { children: [title, shouldShowCaret && _jsx(Caret, {})] }) })), splitButton && (_jsx(SplitCaretButton, { id: id, bsStyle: bsStyle, bsSize: bsSize, disabled: disabled, className: toggleClassName, onClick: toggleOpen, ref: setRefSplitButtonToggle })), isOpen && usePortal && ReactDOM.createPortal(dropdownMenu, dropdownRoot), isOpen && !usePortal && dropdownMenu] })));
});
export default ButtonDropdown;
